import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'antd';
import { PencilIcon, StarFilledIcon, StarIcon, TrashIcon } from './lucideIcons';
import style from './index.scss';
import EventIcon from './EventIcon';
import { canonicalIconKey } from '../../trm/smartAssessment/FramlAlertDetailShell/components/eventIcons';
import IconPicker from './IconPicker';
import { iconLabel } from './iconLabel';
import { EVENT_CATEGORY, isSystemType } from './constants';

/**
 * The header strip above the field table:
 *
 *   [icon] Login  `activity_type = login`  ★ Default  ✎  🗑     Event icon [LogIn]
 *
 * The MATCHING RULE is shown as code, not prose, because it is the one value that
 * must equal the client payload exactly — §15 makes it read-only after creation
 * for that reason, so the header states it rather than offering it for edit.
 */
const EventTypeHeader = ({
  type,
  category,
  onEdit,
  onDelete,
  onToggleDefault,
  onPickIcon,
  iconPickerOpen,
  onPickIconCancel,
  onIconPicked
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  /* AC-1: "No Edit name, Delete, or Set as default actions are available for the
     system-default type."
     ABSENT rather than disabled, matching the identifier row's delete in
     FieldConfigTable — a greyed control invites the click and then refuses it.
     The backend refuses all three anyway (delete answers "System default event
     types cannot be deleted"), so rendering them only converts a rule into an
     error message. */
  const systemType = isSystemType(type);

  const isActivity = category === EVENT_CATEGORY.ACTIVITY;
  const keyName = isActivity ? 'activity_type' : 'txn_type';

  return (
    <div className={style.typeHeader}>
      <EventIcon
        iconKey={type.iconKey}
        technicalValue={type.technicalValue}
        className={style.typeHeaderIcon}
      />
      {/* `title` for the same reason as the match-rule chip below: once the
          name clips, the full value must stay reachable. */}
      <span className={style.typeHeaderName} title={type.displayName}>
        {type.displayName}
      </span>

      {/* `title` so the full value stays reachable once .matchRule clips it —
          this is the one string that must match ingestion character for
          character, so it must never become unreadable. */}
      <code
        className={style.matchRule}
        title={`${keyName} = ${type.technicalValue}`}
      >
        {keyName} = {type.technicalValue}
      </code>

      {/* The design badges the system type in the HEADER as well as in the left
          list — the two surfaces scroll independently, and the header is the one
          still on screen once the field table is scrolled, which is exactly when
          "why can I not delete this?" gets asked. Same muted pill as the list. */}
      {systemType ? (
        <span className={style.systemBadge}>System default</span>
      ) : null}

      {/* One default per category. Toggling ON is what the page has to police —
          it must clear the previous default, or two types both claim the
          fallback and which one wins becomes a race. */}
      {/* NO TOOLTIP — REMOVED 2026-09-09, and deliberately not to be re-added.
          It read "Make this the fallback for unconfigured event types" and was
          INVENTED here on 2026-09-02: no PRD, story or design specifies any
          hover copy for this control, and nothing asserted it. It also covered
          the page description above whenever the button was hovered.
          The button's own two labels — "Set as Default" / "Default" — already
          say what it does. Third removal of the same kind on this screen, after
          "Must match the value in your payload exactly" and "This identifier
          field is always shown": undocumented helper copy does not belong here
          unless a spec asks for it. */}
      {systemType ? null : (
        <button
          type="button"
          className={`${style.defaultToggle} ${
            type.isDefault ? style.defaultToggleOn : ''
          }`}
          aria-pressed={type.isDefault}
          onClick={() => onToggleDefault(type.id)}
        >
          {type.isDefault ? <StarFilledIcon /> : <StarIcon />}
          {/* Two labels, as the design has: the button INVITES the action when
              the type is not the default, and STATES the fact when it is.
              A bare "Default" on both read as a label rather than a control, so
              there was nothing telling you it could be clicked. */}
          {type.isDefault ? 'Default' : 'Set as Default'}
        </button>
      )}

      {systemType ? null : (
        <button
          type="button"
          className={style.headerAction}
          aria-label={`Edit ${type.displayName}`}
          onClick={() => onEdit(type.id)}
        >
          <PencilIcon />
        </button>
      )}

      {/* Confirmed, because §16 removes the type's whole field configuration —
          recoverable only by reconfiguring it by hand. The copy says what is and
          is not destroyed, since "delete" on a fraud screen reasonably raises the
          question of whether the events go too. */}
      {/* A MODAL, not a Popconfirm bubble. The design confirms this in a centred
          dialog with a generic title and the TECHNICAL VALUE in the body —
          "This removes `login` and its field configuration" — because that is
          the thing actually being deleted; the display name is just a label.
          Checked against the design 2026-09-03. */}
      {systemType ? null : (
        <button
          type="button"
          className={`${style.headerAction} ${style.headerActionDanger}`}
          aria-label={`Delete ${type.displayName}`}
          onClick={() => setConfirmOpen(true)}
        >
          <TrashIcon />
        </button>
      )}

      <Modal
        className={style.settingsModal}
        width={448}
        maskStyle={{ background: 'rgba(0, 0, 0, 0.8)' }}
        title={`Remove this ${isActivity ? 'activity' : 'transaction'} type?`}
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onOk={() => {
          setConfirmOpen(false);
          onDelete(type.id);
        }}
        okText="Remove"
        cancelText="Cancel"
        /* PRIMARY, not danger. The design's Remove button is the standard blue —
           deliberate, since this removes a display configuration, not data. The
           body says so. */
        okButtonProps={{ className: style.primaryButton }}
        destroyOnClose
      >
        <p className={style.modalIntro}>
          This removes <code>{type.technicalValue}</code> and its field
          configuration. Built-in types are unaffected.
        </p>
      </Modal>

      <span className={style.iconPickerSlot}>
        <span className={style.iconPickerLabel}>Event icon</span>
        {/* THE BUTTON IS THE POPOVER'S ANCHOR. The design measures the panel
            against this element — `--radix-popper-anchor-width: 89.890625px`,
            `--radix-popper-anchor-height: 36px` — so the picker has to wrap it
            rather than be rendered elsewhere on the page. */}
        <IconPicker
          open={iconPickerOpen}
          current={type.iconKey}
          onPick={onIconPicked}
          onCancel={onPickIconCancel}
        >
          <Button onClick={() => onPickIcon(type.id)}>
            <EventIcon
              iconKey={type.iconKey}
              technicalValue={type.technicalValue}
            />
            {/* NORMALISED: `iconKey` may still be a pre-rekey value, and
                labelling the raw key put "Desktop" next to a monitor glyph.
                EventIcon above already resolves through the same function, so
                this keeps the two halves of the button describing one icon. */}
            <span className={style.iconPickerName}>
              {iconLabel(canonicalIconKey(type.iconKey))}
            </span>
          </Button>
        </IconPicker>
      </span>
    </div>
  );
};

EventTypeHeader.propTypes = {
  type: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    displayName: PropTypes.string.isRequired,
    technicalValue: PropTypes.string.isRequired,
    iconKey: PropTypes.string,
    isDefault: PropTypes.bool,
    /** Set by the BE as `technical_value == "default"` — see isSystemType. */
    isSystem: PropTypes.bool
  }).isRequired,
  category: PropTypes.oneOf(Object.values(EVENT_CATEGORY)).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleDefault: PropTypes.func.isRequired,
  onPickIcon: PropTypes.func.isRequired,
  /** Whether the icon popover is open. Owned by the screen, not the header. */
  iconPickerOpen: PropTypes.bool,
  onPickIconCancel: PropTypes.func.isRequired,
  onIconPicked: PropTypes.func.isRequired
};

export default EventTypeHeader;
